import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useValuations } from "../hooks/useApi";
import DynamicFormRenderer from "../components/forms/DynamicFormRenderer";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ErrorDisplay from "../components/ui/ErrorDisplay";
import { StatusBadge } from "../components/ui/StatusIndicators";
import {
  TrashIcon,
  PencilIcon,
  EyeIcon,
  HomeIcon,
  ChevronRightIcon,
  CurrencyPoundIcon,
  MapPinIcon,
  CalendarIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";

function ValuationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    items: valuations,
    loading,
    error,
    updateItem,
    deleteItem,
  } = useValuations();

  const [valuation, setValuation] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);

  // Find the valuation
  useEffect(() => {
    if (valuations && id) {
      const foundValuation = valuations.find((v) => v.id === id);
      setValuation(foundValuation);

      if (foundValuation) {
        setFormData(transformValuationToFormData(foundValuation));
      }
    }
  }, [valuations, id]);

  // Data transformation: API data to form format
  const transformValuationToFormData = (valuation) => {
    // Handle Step One valuation format
    if (valuation.template_id === "valuation_step_one_v1") {
      return {
        // Basic Information (top level)
        name: valuation.name || "",
        description: valuation.description || "",
        status: valuation.status || "draft",
        type: valuation.type || "",
        purpose: valuation.purpose || "",

        // Valuation Overview section
        valuationId: valuation["valuation-overview"]?.["valuation-id"] || "",
        lender: valuation["valuation-overview"]?.["lender"] || "",
        "complies-guidelines":
          valuation["valuation-overview"]?.[
            "complies-with-lender-guidelines"
          ] || false,
        "mortgageable-majority-lenders":
          valuation["valuation-overview"]?.[
            "mortgageable-by-mainstream-lenders"
          ] || false,
        "demand-inferior":
          valuation["risks"]?.["inferior-demand-on-resale"] || false,
        "general-remarks":
          valuation["valuation-overview"]?.["valuation-description"] || "",
        "comparable-commentary":
          valuation["comparables"]?.["comparable-commentary"] || "",

        // Property Details section
        "property-address":
          `${valuation["property-details"]?.["address-line-1"] || ""}, ${valuation["property-details"]?.["town-city"] || ""}, ${valuation["property-details"]?.["postcode"] || ""}`.replace(
            /^, |, $|, , /g,
            ""
          ),
        "property-country": valuation["property-details"]?.["country"] || "",
        "property-area-type":
          valuation["property-details"]?.["location-predominantly"] || "",
        "property-above-or-adjacent-commercial":
          valuation["property-details"]?.[
            "above-adjacent-commercial-property"
          ] || false,
        "property-type": valuation["property-details"]?.["property-type"] || "",
        "property-style":
          valuation["property-details"]?.["property-style"] || "",
        "purpose-built":
          valuation["property-details"]?.["purpose-built"] || false,
        "ex-local-authority":
          valuation["property-details"]?.["ex-local-authority"] || false,
        "approx-year-built":
          valuation["property-details"]?.["approximate-year-built"] || "",
        "gross-external-floor-area-m2":
          valuation["property-details"]?.["gross-external-floor-area-m2"] || "",
        "significant-additions":
          valuation["property-details"]?.[
            "significant-additions-alterations"
          ] || false,

        // Accommodation & Construction section
        floors: valuation["accommodation-construction"]?.["floors"] || "",
        "living-rooms":
          valuation["accommodation-construction"]?.["living-rooms"] || "",
        kitchens: valuation["accommodation-construction"]?.["kitchens"] || "",
        bedrooms: valuation["accommodation-construction"]?.["bedrooms"] || "",
        "bath-shower-rooms":
          valuation["accommodation-construction"]?.["bathrooms"] || "",
        "total-internal-wc":
          valuation["accommodation-construction"]?.["total-internal-wcs"] || "",
        garage: valuation["accommodation-construction"]?.["garage"] || "",
        "private-parking":
          valuation["accommodation-construction"]?.["private-parking"] || "",
        "parking-outside-curtilage":
          valuation["accommodation-construction"]?.[
            "parking-outside-curtilage"
          ] || false,
        "construction-standard":
          valuation["accommodation-construction"]?.["standard-construction"] ||
          false,
        "area-flat-roof-exceeds-25-percent":
          valuation["accommodation-construction"]?.[
            "flat-roof-area-exceeds-25-percent"
          ] || false,
        "services-mains-water-electric-drainage":
          valuation["accommodation-construction"]?.[
            "mains-services-available"
          ] || false,

        // Tenure & Legal section
        tenure: valuation["tenure-legal"]?.["tenure"] || "",
        "leasehold-years-remaining": "",
        "service-maintenance-charges": "",
        occupation:
          valuation["tenure-legal"]?.["occupation-applicant"] || false,
        "applicant-occupies-more-than-40-percent":
          valuation["tenure-legal"]?.["applicant-occupies-40-percent"] || false,
        "tenancies-present":
          valuation["tenure-legal"]?.["tenancies-aware"] || false,
        "non-residential-use":
          valuation["tenure-legal"]?.["non-residential-use"] || false,
        "solar-panels-present":
          valuation["tenure-legal"]?.["solar-panels-present"] || false,
        "other-legal-matters-present":
          valuation["tenure-legal"]?.["other-legal-matters"] || false,
        "other-legal-matters-details":
          valuation["tenure-legal"]?.["other-legal-matters-details"] || "",

        // Valuation Figures section
        marketValuePresentConditionGbp:
          valuation["valuation-figures"]?.[
            "market-value-present-condition-gbp"
          ] || "",
        marketValueWithEssentialRepairsGbp:
          valuation["valuation-figures"]?.["essential-repairs-value-gbp"] || "",
        retentionRequired:
          valuation["valuation-figures"]?.["retention-required"] || false,
        retentionAmountGbp:
          valuation["valuation-figures"]?.["amount-of-retention-gbp"] || "",
        insuranceReinstatementValueGbp:
          valuation["valuation-figures"]?.["insurance-reinstatement-gbp"] || "",

        // Risks section
        "flood-risk": valuation["risks"]?.["risk-of-flooding"] || false,
        "subsidence-heave-landslip-erosion-risk":
          valuation["risks"]?.["subsidence-landslip-risk"] || false,
        "structural-movement-evidence":
          valuation["risks"]?.["structural-movement-evidence"] || false,

        // Specialist Reports section
        "timber-and-damp":
          valuation["specialist-reports"]?.["timber-damp"] || false,
        "wall-tie": valuation["specialist-reports"]?.["wall-tie"] || false,
        "structural-report":
          valuation["specialist-reports"]?.["structural"] || false,
        "arboricultural-report":
          valuation["specialist-reports"]?.["arboricultural"] || false,
        "other-specialist-report":
          valuation["specialist-reports"]?.["other"] || false,
        "other-specialist-report-details":
          valuation["specialist-reports"]?.["other-details"] || "",

        // Photographs section
        "photo-internal-kitchen":
          valuation["photographs"]?.["internal-areas"]?.["kitchen"] || false,
        "photo-internal-bathroom":
          valuation["photographs"]?.["internal-areas"]?.["bathroom"] || false,
        "photo-internal-bedrooms":
          valuation["photographs"]?.["internal-areas"]?.["bedroom"] || false,
        "photo-internal-main-living-area":
          valuation["photographs"]?.["internal-areas"]?.["main-living-area"] ||
          false,
        "photo-external-front-elevation":
          valuation["photographs"]?.["outside"]?.["front-elevation"] || false,
        "photo-external-rear-elevation":
          valuation["photographs"]?.["outside"]?.["rear-elevation"] || false,
        "photo-external-street-scene":
          valuation["photographs"]?.["outside"]?.["street-scene"] || false,
        "photo-pages":
          valuation["photographs"]?.["photo-files"]?.join(", ") || "",

        // Comparables section (simplified for now)
        "comparables-array": JSON.stringify(
          valuation["comparables"]?.["entries"] || []
        ),

        // Valuer Information section
        "valuer-name": valuation["valuer-information"]?.["valuer-name"] || "",
        "rics-number": valuation["valuer-information"]?.["rics-number"] || "",
        "valuer-qualifications":
          valuation["valuer-information"]?.["rics-qualifications"] || "",
        "signature-security-code":
          valuation["valuer-information"]?.["signature-security-code"] || "",
        "firm-name": valuation["valuer-information"]?.["valuer-firm"] || "",
        telephone:
          valuation["valuer-information"]?.["valuer-contact-number"] || "",
        "firm-address":
          valuation["valuer-information"]?.["valuer-office-address"] || "",

        // Dates & Timeline section
        "inspection-date":
          valuation["dates-timeline"]?.["date-inspected"] || "",
        "report-date": valuation["dates-timeline"]?.["date-reported"] || "",
      };
    }

    // Handle Basic valuation format (existing logic)
    return {
      // Basic Information
      name: valuation.name || "",
      description: valuation.description || "",
      status: valuation.status || "draft",
      type: valuation.type || "",
      purpose: valuation.purpose || "",
      relatedProject: valuation.relatedProject || "",
      relatedDeal: valuation.relatedDeal || "",

      // Property Details
      property_address: valuation.property?.address || "",
      property_tenure: valuation.property?.tenure || "",
      property_site_area: valuation.property?.siteArea || "",
      property_site_area_unit: valuation.property?.siteAreaUnit || "acres",
      property_planning_status: valuation.property?.planningStatus || "",
      property_existing_use: valuation.property?.existingUse || "",
      property_proposed_use: valuation.property?.proposedUse || "",

      // Financial Valuation
      valuation_market_value: valuation.valuation?.marketValue || "",
      valuation_gdv: valuation.valuation?.gdv || "",
      valuation_total_costs: valuation.valuation?.totalCosts || "",
      valuation_residual_land_value:
        valuation.valuation?.residualLandValue || "",
      valuation_profit: valuation.valuation?.profit || "",
      valuation_profit_margin: valuation.valuation?.profitMargin || "",
      valuation_profit_on_cost: valuation.valuation?.profitOnCost || "",
      valuation_land_value: valuation.valuation?.landValue || "",

      // Cost Breakdown
      costs_land_cost: valuation.costs?.landCost || "",
      costs_build_costs: valuation.costs?.buildCosts || "",
      costs_professional_fees: valuation.costs?.professionalFees || "",
      costs_planning_costs: valuation.costs?.planningCosts || "",
      costs_marketing: valuation.costs?.marketing || "",
      costs_legal: valuation.costs?.legal || "",
      costs_finance: valuation.costs?.finance || "",
      costs_contingency: valuation.costs?.contingency || "",

      // Revenue Analysis
      revenue_total_gdv:
        valuation.revenue?.totalGDV || valuation.valuation?.gdv || "",
      revenue_unit_mix: valuation.revenue?.units
        ? valuation.revenue.units
            .map((unit) => `${unit.quantity}x ${unit.type}`)
            .join(", ")
        : "",
      revenue_average_unit_price: valuation.revenue?.units?.length
        ? Math.round(
            valuation.revenue.units.reduce(
              (sum, unit) => sum + unit.unitPrice,
              0
            ) / valuation.revenue.units.length
          )
        : "",
      revenue_price_per_sqft: valuation.revenue?.units?.length
        ? Math.round(
            valuation.revenue.units.reduce(
              (sum, unit) => sum + unit.pricePerSqFt,
              0
            ) / valuation.revenue.units.length
          )
        : "",
      revenue_sales_strategy: "",

      // Assumptions & Methodology
      methodology: valuation.methodology || "",
      basis: valuation.basis || "",
      assumptions_build_rate: valuation.assumptions?.buildRate || "",
      assumptions_sale_rate: valuation.assumptions?.saleRate || "",
      assumptions_construction_period:
        valuation.assumptions?.constructionPeriod || "",
      assumptions_sales_period: valuation.assumptions?.salesPeriod || "",
      assumptions_finance_rate: valuation.assumptions?.financeRate || "",
      assumptions_contingency: valuation.assumptions?.contingency || "",
      assumptions_market_conditions: valuation.marketConditions || "",
      assumptions_confidence_level: valuation.confidence || "",

      // Valuer Information
      valuer_firm: valuation.valuer?.firm || "",
      valuer_surveyor: valuation.valuer?.surveyor || "",
      valuer_qualification: valuation.valuer?.qualification || "",
      valuer_contact: valuation.valuer?.contact || "",
      valuer_inspection_type: valuation.inspectionType || "",

      // Dates & Timeline
      dates_instructed: valuation.dates?.instructed || "",
      dates_inspected: valuation.dates?.inspected || "",
      dates_reported: valuation.dates?.reported || "",
      dates_reviewed: valuation.dates?.reviewed || "",
      dates_approved: valuation.dates?.approved || "",
      dates_valid_until: valuation.dates?.validUntil || "",
    };
  };

  // Data transformation: Form format to API data
  const transformFormDataToValuation = (data) => {
    // Handle Step One valuation format
    if (valuation.template_id === "valuation_step_one_v1") {
      return {
        name: data.name,
        description: data.description,
        status: data.status,
        type: data.type,
        purpose: data.purpose,
        template_id: valuation.template_id,

        "valuation-overview": {
          "valuation-id": data.valuationId,
          "valuation-name": "Step One Mortgage Valuation Report",
          "valuation-description": data["general-remarks"],
          lender: data["lender"],
          "complies-with-lender-guidelines": data["complies-guidelines"],
          "mortgageable-by-mainstream-lenders":
            data["mortgageable-majority-lenders"],
          "market-value-present-condition-gbp":
            Number(data["market-value-present-condition-gbp"]) || null,
          "essential-repairs-value-gbp":
            Number(data["market-value-with-essential-repairs-gbp"]) || null,
          "retention-required": data["retention-required"],
          "amount-of-retention-gbp":
            Number(data["retention-amount-gbp"]) || null,
          "insurance-reinstatement-gbp":
            Number(data["insurance-reinstatement-value-gbp"]) || null,
        },

        "property-details": {
          // Parse the combined address back to components
          "address-line-1": data["property-address"]?.split(", ")[0] || "",
          "town-city": data["property-address"]?.split(", ")[1] || "",
          postcode: data["property-address"]?.split(", ")[2] || "",
          country: data["property-country"],
          "location-predominantly": data["property-area-type"],
          "above-adjacent-commercial-property":
            data["property-above-or-adjacent-commercial"],
          "property-type": data["property-type"],
          "property-style": data["property-style"],
          "purpose-built": data["purpose-built"],
          "ex-local-authority": data["ex-local-authority"],
          "approximate-year-built": Number(data["approx-year-built"]) || null,
          "gross-external-floor-area-m2":
            Number(data["gross-external-floor-area-m2"]) || null,
          "significant-additions-alterations": data["significant-additions"],
        },

        "accommodation-construction": {
          floors: Number(data["floors"]) || null,
          "living-rooms": Number(data["living-rooms"]) || null,
          kitchens: Number(data["kitchens"]) || null,
          bedrooms: Number(data["bedrooms"]) || null,
          bathrooms: Number(data["bath-shower-rooms"]) || null,
          "total-internal-wcs": Number(data["total-internal-wc"]) || null,
          garage: data["garage"],
          "private-parking": data["private-parking"],
          "parking-outside-curtilage": data["parking-outside-curtilage"],
          "standard-construction": data["construction-standard"],
          "flat-roof-area-exceeds-25-percent":
            data["area-flat-roof-exceeds-25-percent"],
          "mains-services-available":
            data["services-mains-water-electric-drainage"],
        },

        "tenure-legal": {
          tenure: data["tenure"],
          "occupation-applicant": data["occupation"],
          "applicant-occupies-40-percent":
            data["applicant-occupies-more-than-40-percent"],
          "tenancies-aware": data["tenancies-present"],
          "non-residential-use": data["non-residential-use"],
          "solar-panels-present": data["solar-panels-present"],
          "other-legal-matters": data["other-legal-matters-present"],
          "other-legal-matters-details": data["other-legal-matters-details"],
        },

        "valuation-figures": {
          "market-value-present-condition-gbp":
            Number(data.marketValuePresentConditionGbp) || null,
          "essential-repairs-value-gbp":
            Number(data.marketValueWithEssentialRepairsGbp) || null,
          "retention-required": data.retentionRequired,
          "amount-of-retention-gbp": Number(data.retentionAmountGbp) || null,
          "insurance-reinstatement-gbp":
            Number(data.insuranceReinstatementValueGbp) || null,
        },

        risks: {
          "inferior-demand-on-resale": data["demand-inferior"],
          "risk-of-flooding": data["flood-risk"],
          "subsidence-landslip-risk":
            data["subsidence-heave-landslip-erosion-risk"],
          "structural-movement-evidence": data["structural-movement-evidence"],
        },

        "specialist-reports": {
          "timber-damp": data["timber-and-damp"],
          "wall-tie": data["wall-tie"],
          structural: data["structural-report"],
          arboricultural: data["arboricultural-report"],
          other: data["other-specialist-report"],
          "other-details": data["other-specialist-report-details"],
        },

        photographs: {
          "internal-areas": {
            kitchen: data["photo-internal-kitchen"],
            bathroom: data["photo-internal-bathroom"],
            bedroom: data["photo-internal-bedrooms"],
            "main-living-area": data["photo-internal-main-living-area"],
          },
          outside: {
            "front-elevation": data["photo-external-front-elevation"],
            "rear-elevation": data["photo-external-rear-elevation"],
            "street-scene": data["photo-external-street-scene"],
          },
          "photo-files":
            data["photo-pages"]?.split(", ").filter((f) => f.trim()) || [],
        },

        comparables: {
          entries: JSON.parse(data["comparables-array"] || "[]"),
          "comparable-commentary": data["comparable-commentary"],
        },

        "valuer-information": {
          "valuer-name": data["valuer-name"],
          "rics-number": data["rics-number"],
          "rics-qualifications": data["valuer-qualifications"],
          "signature-security-code": data["signature-security-code"],
          "valuer-firm": data["firm-name"],
          "valuer-contact-number": data["telephone"],
          "valuer-office-address": data["firm-address"],
        },

        "dates-timeline": {
          "date-instructed": null,
          "date-inspected": data["inspection-date"],
          "date-reported": data["report-date"],
          "date-reviewed": null,
          "date-approved": null,
          "valid-until": null,
        },
      };
    }

    // Handle Basic valuation format (existing logic)
    return {
      name: data.name,
      description: data.description,
      status: data.status,
      type: data.type,
      purpose: data.purpose,
      relatedProject: data.relatedProject,
      relatedDeal: data.relatedDeal,
      template_id: valuation.template_id, // Preserve template_id

      property: {
        address: data["property_address"],
        tenure: data["property_tenure"],
        siteArea: parseFloat(data["property_site_area"]) || 0,
        siteAreaUnit: data["property_site_area_unit"],
        planningStatus: data["property_planning_status"],
        existingUse: data["property_existing_use"],
        proposedUse: data["property_proposed_use"],
      },

      valuation: {
        marketValue: parseFloat(data["valuation_market_value"]) || 0,
        gdv: parseFloat(data["valuation_gdv"]) || 0,
        totalCosts: parseFloat(data["valuation_total_costs"]) || 0,
        residualLandValue:
          parseFloat(data["valuation_residual_land_value"]) || 0,
        profit: parseFloat(data["valuation_profit"]) || 0,
        profitMargin: parseFloat(data["valuation_profit_margin"]) || 0,
        profitOnCost: parseFloat(data["valuation_profit_on_cost"]) || 0,
        landValue: parseFloat(data["valuation_land_value"]) || 0,
      },

      costs: {
        landCost: parseFloat(data["costs_land_cost"]) || 0,
        buildCosts: parseFloat(data["costs_build_costs"]) || 0,
        professionalFees: parseFloat(data["costs_professional_fees"]) || 0,
        planningCosts: parseFloat(data["costs_planning_costs"]) || 0,
        marketing: parseFloat(data["costs_marketing"]) || 0,
        legal: parseFloat(data["costs_legal"]) || 0,
        finance: parseFloat(data["costs_finance"]) || 0,
        contingency: parseFloat(data["costs_contingency"]) || 0,
      },

      revenue: {
        totalGDV:
          parseFloat(data["revenue_total_gdv"]) ||
          parseFloat(data["valuation_gdv"]) ||
          0,
      },

      assumptions: {
        buildRate: parseFloat(data["assumptions_build_rate"]) || 0,
        saleRate: parseFloat(data["assumptions_sale_rate"]) || 0,
        constructionPeriod:
          parseInt(data["assumptions_construction_period"]) || 0,
        salesPeriod: parseInt(data["assumptions_sales_period"]) || 0,
        financeRate: parseFloat(data["assumptions_finance_rate"]) || 0,
        contingency: parseFloat(data["assumptions_contingency"]) || 0,
      },

      valuer: {
        firm: data["valuer_firm"],
        surveyor: data["valuer_surveyor"],
        qualification: data["valuer_qualification"],
        contact: data["valuer_contact"],
      },

      dates: {
        instructed: data["dates_instructed"],
        inspected: data["dates_inspected"],
        reported: data["dates_reported"],
        reviewed: data["dates_reviewed"],
        approved: data["dates_approved"],
        validUntil: data["dates_valid_until"],
      },

      methodology: data.methodology,
      basis: data.basis,
      marketConditions: data["assumptions_market_conditions"],
      confidence: data["assumptions_confidence_level"],
      inspectionType: data["valuer_inspection_type"],
    };
  };

  const handleSave = async (data) => {
    try {
      const updatedValuation = transformFormDataToValuation(data);
      await updateItem(id, updatedValuation);
      setIsEditing(false);
      // Refresh the valuation data
      if (valuations) {
        const refreshedValuation = valuations.find((v) => v.id === id);
        if (refreshedValuation) {
          setValuation({ ...refreshedValuation, ...updatedValuation });
        }
      }
    } catch (error) {
      console.error("Failed to save valuation:", error);
      alert("Failed to save valuation. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete "${valuation.name}"? This action cannot be undone.`
      )
    ) {
      setIsDeleting(true);
      try {
        await deleteItem(id);
        navigate("/valuations");
      } catch (error) {
        console.error("Failed to delete valuation:", error);
        alert("Failed to delete valuation. Please try again.");
        setIsDeleting(false);
      }
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "Not specified";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="large" message="Loading valuation..." />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="py-12">
        <ErrorDisplay
          error={error}
          message="Failed to load valuation"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  // Handle valuation not found
  if (!loading && !valuation) {
    return (
      <div className="py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Valuation Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The valuation with ID "{id}" could not be found.
          </p>
          <Link to="/valuations" className="btn-primary">
            ← Back to Valuations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/" className="hover:text-gray-700">
              <HomeIcon className="w-4 h-4" />
            </Link>
          </li>
          <ChevronRightIcon className="w-4 h-4" />
          <li>
            <Link to="/valuations" className="hover:text-gray-700">
              Valuations
            </Link>
          </li>
          <ChevronRightIcon className="w-4 h-4" />
          <li className="text-gray-900 font-medium truncate">
            {valuation.name}
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold text-gray-900 truncate">
            {valuation.name}
          </h1>
          <div className="mt-2 flex items-center space-x-4">
            <StatusBadge status={valuation.status} />
            <span className="text-sm text-gray-500">
              {valuation.type
                ?.split("-")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")}
            </span>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {!isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="btn-secondary"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn-danger"
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(false)}
              className="btn-secondary"
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              View Mode
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {!isEditing && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <CurrencyPoundIcon className="w-8 h-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">
                  Market Value
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {valuation.template_id === "valuation_step_one_v1"
                    ? formatCurrency(
                        valuation["valuation-figures"]?.[
                          "market-value-present-condition-gbp"
                        ]
                      )
                    : formatCurrency(valuation.valuation?.marketValue)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <BuildingOfficeIcon className="w-8 h-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">
                  {valuation.template_id === "valuation_step_one_v1"
                    ? "Property Type"
                    : "GDV"}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {valuation.template_id === "valuation_step_one_v1"
                    ? valuation["property-details"]?.["property-type"] ||
                      "Not specified"
                    : formatCurrency(valuation.valuation?.gdv)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <MapPinIcon className="w-8 h-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">
                  {valuation.template_id === "valuation_step_one_v1"
                    ? "Size"
                    : "Property"}
                </p>
                <p className="text-lg font-semibold text-gray-900 truncate">
                  {valuation.template_id === "valuation_step_one_v1"
                    ? valuation["property-details"]?.[
                        "gross-external-floor-area-m2"
                      ]
                      ? `${valuation["property-details"]["gross-external-floor-area-m2"]} m²`
                      : "Not specified"
                    : valuation.property?.address || "Not specified"}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <CalendarIcon className="w-8 h-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Reported</p>
                <p className="text-lg font-semibold text-gray-900">
                  {valuation.template_id === "valuation_step_one_v1"
                    ? valuation["dates-timeline"]?.["date-reported"]
                      ? new Date(
                          valuation["dates-timeline"]["date-reported"]
                        ).toLocaleDateString()
                      : "Not specified"
                    : valuation.dates?.reported
                      ? new Date(valuation.dates.reported).toLocaleDateString()
                      : "Not specified"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="card">
        {Object.keys(formData).length > 0 ? (
          <DynamicFormRenderer
            key={`valuation-${id}-${isEditing ? "edit" : "view"}`}
            templateId={valuation.template_id || "valuation-basic-v1"}
            fieldsId="valuation-fields"
            data={formData}
            onDataChange={setFormData}
            onSave={handleSave}
            readOnly={!isEditing}
            autoSave={false}
            showProgress={true}
          />
        ) : (
          <div className="flex justify-center py-8">
            <LoadingSpinner message="Loading valuation data..." />
          </div>
        )}
      </div>
    </div>
  );
}

export default ValuationDetail;
